import * as m from "motion/react-m";
import { ScrollArea } from "@base-ui/react/scroll-area";
import { AnimatePresence } from "motion/react";
import clsx from "clsx";
import { BaseLinkTracked } from "@ui/components/core/LinkTracked";
import { EventName } from "@mw/shared/analyticsEventNames";

function getClasses(color: "blue" | "red") {
  if (color === "red") {
    return {
      bg: "tw-from-red-50",
      border: "tw-border-red-200",
      text: "tw-text-red-800",
      icon: "tw-text-red-600",
      thumb: "tw-bg-red-800/25",
      link: "tw-decoration-red-800/30 group-hover:tw-decoration-red-800",
    };
  }

  return {
    bg: "tw-from-blue-50",
    border: "tw-border-blue-200",
    text: "tw-text-blue-800",
    icon: "tw-text-blue-600",
    thumb: "tw-bg-blue-800/25",
    link: "tw-decoration-blue-800/30 group-hover:tw-decoration-blue-800",
  };
}

interface LinkOptions {
  href: string;
  eventName: EventName;
  eventProperties?: Record<string, unknown>;
  target?: React.HTMLAttributeAnchorTarget;
  rel?: string;
}

function LinkOrDiv({ link, ...props }: { children: string; link?: LinkOptions; className?: string }) {
  if (link) {
    return (
      <BaseLinkTracked
        href={link.href}
        target={link.target}
        rel={link.rel}
        eventName={link.eventName}
        eventProperties={link.eventProperties}
        {...props}
      >
        {props.children}
        <span className='tw-absolute tw-inset-0' />
      </BaseLinkTracked>
    );
  }

  return <div {...props} />;
}

export function Footer({
  children,
  color = "blue",
  icon: Icon,
  link,
  hidden = false,
  className,
}: {
  children: string | null;
  color?: "blue" | "red";
  icon?: React.ComponentType<React.ComponentPropsWithoutRef<"svg">>;
  link?: LinkOptions;
  hidden?: boolean;
  className?: string;
}) {
  const visible = !hidden;
  const classes = getClasses(color);

  return (
    <AnimatePresence initial={false}>
      {visible && children !== null && (
        <m.div
          initial={{ maskPosition: "0% 100%" }}
          animate={{ maskPosition: "0% 0%" }}
          exit={{ maskPosition: "0% 100%" }}
          transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
          style={{
            maskSize: "100% 300%",
            maskImage: "linear-gradient(to bottom, white 33.333333%, transparent 66.666666%)",
          }}
          className={clsx(
            "tw-group tw-absolute tw-top-full tw-inset-x-0 tw-bg-gradient-to-t tw-to-white -tw-mt-6 tw-rounded-b-3xl",
            classes.bg,
            className,
          )}
        >
          <div
            className={clsx(
              "tw-absolute tw-inset-0 tw-rounded-b-3xl tw-border-0.5 tw-pointer-events-none",
              classes.border,
            )}
            style={{ maskImage: "linear-gradient(to top, white, transparent)" }}
          />
          <div
            className='tw-absolute tw-inset-0 tw-rounded-b-3xl tw-border-x-0.5 tw-border-gray-200 tw-pointer-events-none'
            style={{ maskImage: "linear-gradient(to top, transparent, white)" }}
          />
          <ScrollArea.Root>
            <ScrollArea.Viewport
              className='tw-flex'
              style={{
                maskImage:
                  "linear-gradient(to right, transparent, white 1.25rem, white calc(100% - 1.25rem), transparent)",
              }}
            >
              <div className='tw-flex-none tw-min-w-full tw-flex tw-relative tw-px-5 tw-pt-7 tw-pb-4'>
                <AnimatePresence mode='wait' initial={false}>
                  <m.div
                    key={children}
                    className='tw-min-w-full tw-flex-none tw-flex tw-items-center tw-gap-1.5'
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    {Icon && (
                      <Icon
                        aria-hidden
                        className={clsx("tw-ml-auto tw-size-4 tw-relative tw-flex-none", classes.icon)}
                      />
                    )}
                    <LinkOrDiv
                      link={link}
                      className={clsx(
                        "tw-flex-none tw-text-xs/[1rem] tw-whitespace-nowrap",
                        Icon ? "tw-mr-auto" : "tw-mx-auto",
                        classes.text,
                        link && [
                          classes.link,
                          "tw-underline tw-underline-offset-2 tw-transition tw-outline-none focus-visible:tw-outline-focus tw-rounded tw-outline-offset-4 focus-visible:tw-no-underline",
                        ],
                      )}
                    >
                      {children}
                    </LinkOrDiv>
                  </m.div>
                </AnimatePresence>
              </div>
            </ScrollArea.Viewport>
            <ScrollArea.Scrollbar
              orientation='horizontal'
              className='tw-group tw-pointer-events-none tw-flex tw-items-end tw-mb-0.5 tw-mx-5 tw-h-2 tw-rounded-full tw-opacity-0 tw-transition-opacity data-[hovering]:tw-pointer-events-auto data-[scrolling]:tw-pointer-events-auto data-[hovering]:tw-opacity-100 data-[scrolling]:tw-opacity-100 data-[hovering]:tw-delay-0 data-[scrolling]:tw-duration-0'
            >
              <ScrollArea.Thumb className={clsx("tw-h-0.5 tw-rounded-full group-hover:tw-h-0.75", classes.thumb)} />
            </ScrollArea.Scrollbar>
          </ScrollArea.Root>
        </m.div>
      )}
    </AnimatePresence>
  );
}
